import { SentimentSatisfiedAlt } from "@assets/icons";
import Grid from '@mui/material/Grid2';
import DeckboardInformationList, {
  DeckboardInformationListValue,
} from "@common/components/ListDetailViews/common/Deckboard/DeckboardDrawer/DeckboardInformationList";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { displayDateTime } from "@common/utils/localTime.utils";
import { canViewElogsAndRevisions } from "@common/utils/user";
import {
  SentimentDissatisfied,
  SentimentSatisfied,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied,
} from "@mui/icons-material";
import { Box, Divider } from "@mui/material";
import { MeetingIconPurpose } from "@pages/Meeting/utils";
import { getMeetingElog } from "@services/meetings";
import { MeetingElog, ProducerService } from "@services/meetings/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

type InformationType = {
  general: DeckboardInformationListValue[];
  time: DeckboardInformationListValue[];
  inCharge: DeckboardInformationListValue[];
  source: DeckboardInformationListValue[];
  recorder: DeckboardInformationListValue[];
};

const Info = () => {
  const { detail } = useContext(RoomsRealtimeContext);
  const [user] = useAuthenticatedUser();
  const canViewElog = canViewElogsAndRevisions(user);
  const [informationData, setInformationData] = useState<InformationType>();
  const [elog, setElog] = useState<MeetingElog>();
  const [qualityData, setQualityData] = useState<
    DeckboardInformationListValue[]
  >([
    {
      label:
        elog?.wrappedUp !== false || elog?.wrappedUp == undefined
          ? ""
          : "Quality Assessment",
      value: (
        <>
          {elog?.wrappedUp !== false ||
            (elog?.wrappedUp == undefined && (
              <Box
                sx={{
                  color: ({ palette }) => palette.background.highlighter,
                }}
              >
                <SentimentVeryDissatisfied />
                <SentimentDissatisfied />
                <SentimentSatisfied />
                <SentimentSatisfiedAlt />
                <SentimentVerySatisfied />
              </Box>
            ))}
        </>
      ),
    },
  ]);

  const { meetingid } = useParams();
  const { t } = useTranslation();

  const parseProducerNotes = () => {
    let note = elog?.note ?? "";
    const regex =
      /((http|https|ftp):\/\/[\w?=&.\/-;#~%-@]+(?![\w\s?&.\/;#~%"=-@]*>))/g;
    const urlsFound = note.match(regex);
    const resultat: React.ReactNode[] = [];
    let before = "";
    (urlsFound ?? []).forEach((value) => {
      [before, note] = note.split(value);
      resultat.push(before);
      resultat.push(<a href={value}>{value}</a>);
    });
    resultat.push(note);
    return resultat;
  };
  const formatParticipantEmails = () => {
    return (
      detail?.meeting?.scheduledMeeting?.emailParticipants
        ?.map((participant) => participant.email)
        ?.join(", ") ?? ""
    );
  };

  const fetchElog = useCallback(async () => {
    setElog(await getMeetingElog(Number(meetingid)));
  }, [meetingid]);

  useEffect(() => {
    if (canViewElog) fetchElog();
  }, [detail?.meeting]);

  useEffect(() => {
    const meeting = detail?.meeting;
    const temp: InformationType = {
      general: [
        { label: "Meeting ID", value: meeting?.meetingId },
        {
          label: "Host MCU",
          value:
            meeting?.scheduledMeeting?.hostMcu &&
            `${meeting?.scheduledMeeting?.hostMcu?.device?.room?.name} (${meeting?.scheduledMeeting?.hostMcu?.device?.ipAddress})`,
        },
        { label: "Meeting Name", value: meeting?.title },
        { label: "Account", value: meeting?.account?.name },
        {
          label: "Producer",
          value:
            meeting?.producer &&
            `${meeting.producer.firstName} ${meeting.producer.lastName}`,
        },
      ],
      time: [
        {
          label: "Setup Time",
          value: displayDateTime(meeting?.setupTime),
        },
        {
          label: "Start Time",
          value: displayDateTime(meeting?.start),
        },
        {
          label: "End Time",
          value: displayDateTime(meeting?.end),
        },
      ],
      inCharge: [
        { label: "Owner", value: meeting?.scheduledMeeting?.ownerEmail },
        {
          label: "Creator",
          value:
            meeting?.scheduledMeeting?.creator &&
            `${meeting?.scheduledMeeting?.creator?.firstName} ${meeting?.scheduledMeeting?.creator?.lastName}`,
        },
        {
          label: "Requester",
          value: meeting?.scheduledMeeting?.requestorEmail,
        },
        {
          label: "Participants",
          value: formatParticipantEmails(),
        },
      ],
      source: [
        { label: "Source", value: meeting?.scheduledMeeting?.meetingSource },
        {
          label: "Purpose",
          value: (
            <Box
              sx={{
                display: "flex",
                alignItems: "end",
              }}
            >
              <MeetingIconPurpose
                purpose={meeting?.scheduledMeeting?.meetingPurpose}
              />
              {meeting?.scheduledMeeting?.meetingPurpose
                ? ` - ${meeting?.scheduledMeeting?.meetingPurpose}`
                : ""}
            </Box>
          ),
        },
      ],
      recorder: [
        {
          label: "Producer Requested",
          value:
            meeting?.scheduledMeeting?.producerService ===
            ProducerService.FullAttendance
              ? `${t("Yes")} (${t("Full Attendance")})`
              : meeting?.scheduledMeeting?.producerService ===
                ProducerService.GreetParticipant
              ? `${t("Yes")} (${t("Greet Participants")})`
              : meeting?.scheduledMeeting?.producerService ===
                ProducerService.MonitorConnection
              ? `${t("Yes")} (${t("Monitor Connections")})`
              : t("No"),
        },
        {
          label: "Recording Requested",
          value: meeting?.scheduledMeeting?.recorded ? t("Yes") : t("No"),
        },
        {
          label: "VIP",
          value: meeting?.vip ? t("Yes") : t("No"),
        },
      ],
    };
    setInformationData(temp);
  }, [detail?.meeting]);

  useEffect(() => {
    if (detail?.meeting?.wrappedUp)
      setQualityData([
        {
          label: "Meeting was produced",
          value: elog?.elogDetail?.produced ? t("Yes") : t("No"),
        },
        {
          label: "Meeting was successful",
          value: elog?.elogDetail?.succeeded ? t("Yes") : t("No"),
        },
        {
          label: "Producer Attendance",
          value: elog?.elogDetail?.producerAttendedFullMeeting
            ? t("Producer attended the full meeting")
            : elog?.elogDetail?.producerMonitoredConnections
            ? t("Producer monitored connections only")
            : (elog?.elogDetail?.producerAttendanceMinutes ?? 0) > 0
            ? t("Producer attended {{count}} mins of the meeting", {
                count: elog?.elogDetail?.producerAttendanceMinutes,
              })
            : t("Producer didn't attend the meeting"),
        },
        {
          label: "Quality Assessment",
          value: (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: ({ palette }) => palette.background.highlighter,

                [`& > svg:nth-of-type(${
                  elog?.elogDetail?.qualityAssessment === undefined
                    ? 0
                    : Math.max(
                        1,
                        Math.ceil(elog?.elogDetail?.qualityAssessment / 2)
                      )
                })`]: {
                  color: ({ palette }) =>
                    [
                      palette.error.main,
                      palette.error.main,
                      palette.warning.main,
                      palette.success.main,
                      palette.success.main,
                    ][
                      elog?.elogDetail?.qualityAssessment === undefined
                        ? 0
                        : Math.floor(elog?.elogDetail?.qualityAssessment / 2)
                    ],
                },
              }}
            >
              <SentimentVeryDissatisfied />
              <SentimentDissatisfied />
              <SentimentSatisfied />
              <SentimentSatisfiedAlt />
              <SentimentVerySatisfied />
            </Box>
          ),
        },
        {
          label: "Producer Notes",
          value: parseProducerNotes(),
        },
      ]);
  }, [elog]);

  const dataSections = [
    informationData?.general,
    informationData?.time,
    informationData?.inCharge,
    informationData?.source,
    informationData?.recorder,
  ];
  const extendedProperties =
    detail?.meeting?.scheduledMeeting?.extendedProperties;
  if (extendedProperties)
    dataSections.push(
      Object.keys(extendedProperties).map((key) => ({
        label: key,
        value: extendedProperties[key],
      }))
    );
  if (elog?.wrappedUp) dataSections.push(qualityData);

  return (
    <Grid
      container
      direction="column"
      sx={{ width: '100%', gap: "1rem", }}
    >
      {dataSections
        .filter((d) => d !== undefined)
        .map((d, i) => (
          <React.Fragment key={i}>
            {i !== 0 && <Divider />}
            <DeckboardInformationList deckboardInformationValue={d} />
          </React.Fragment>
        ))}
    </Grid>
  );
};

export default Info;
